'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { STALE_TIMES, CACHE_TIMES } from '@/providers/QueryProvider'

// =====================================
// Query Keys
// =====================================
export const financialKeys = {
  all: ['financial'] as const,
  
  // Dashboard
  dashboard: (clinicId: string, period: string) => 
    [...financialKeys.all, 'dashboard', clinicId, period] as const,
  
  // Invoices
  invoices: (clinicId: string) => 
    [...financialKeys.all, 'invoices', clinicId] as const,
  invoicesWithFilters: (clinicId: string, status?: string, patientId?: string) => 
    [...financialKeys.invoices(clinicId), status, patientId] as const,
  
  // Transactions
  transactions: (clinicId: string) => 
    [...financialKeys.all, 'transactions', clinicId] as const,
  transactionsWithRange: (clinicId: string, startDate?: string, endDate?: string) => 
    [...financialKeys.transactions(clinicId), startDate, endDate] as const,
  
  // Payroll
  payroll: (clinicId: string, month: string) => 
    [...financialKeys.all, 'payroll', clinicId, month] as const,
  
  // Budget
  budget: (clinicId: string) => 
    [...financialKeys.all, 'budget', clinicId] as const,
}

// =====================================
// Fetch Functions
// =====================================
async function fetchDashboard(clinicId: string, period: string) {
  const response = await fetch(
    `/api/financial/dashboard?clinicId=${clinicId}&period=${period}`
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to fetch dashboard')
  }
  return response.json()
}

async function fetchInvoices(clinicId: string, status?: string, patientId?: string) {
  const params = new URLSearchParams({ clinicId })
  if (status) params.append('status', status)
  if (patientId) params.append('patientId', patientId)
  
  const response = await fetch(`/api/financial/invoices?${params}`)
  if (!response.ok) throw new Error('Failed to fetch invoices')
  return response.json()
}

async function fetchTransactions(clinicId: string, startDate?: string, endDate?: string) {
  const params = new URLSearchParams({ clinicId })
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  
  const response = await fetch(`/api/financial/transactions?${params}`)
  if (!response.ok) throw new Error('Failed to fetch transactions')
  return response.json()
}

async function fetchPayroll(clinicId: string, yearOrPeriod: string) {
  // yearOrPeriod can be "2026" (year) or "2026-02" (YYYY-MM pay period)
  const params = new URLSearchParams({ clinicId, staffId: 'all' })
  if (yearOrPeriod.length === 4) {
    params.set('year', yearOrPeriod)
  } else if (yearOrPeriod.includes('-')) {
    params.set('payPeriod', yearOrPeriod)
  } else {
    params.set('year', yearOrPeriod)
  }
  const response = await fetch(`/api/hr/payroll?${params}`)
  if (!response.ok) throw new Error('Failed to fetch payroll')
  return response.json()
}

// =====================================
// Query Hooks
// =====================================

/**
 * Fetch financial dashboard with 30-minute cache
 */
export function useFinancialDashboard(
  clinicId: string,
  period: string = 'month',
  enabled = true
) {
  return useQuery({
    queryKey: financialKeys.dashboard(clinicId, period),
    queryFn: () => fetchDashboard(clinicId, period),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.CLINIC_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch invoices with 30-minute cache
 */
export function useInvoices(
  clinicId: string,
  status?: string,
  patientId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: financialKeys.invoicesWithFilters(clinicId, status, patientId),
    queryFn: () => fetchInvoices(clinicId, status, patientId),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.CLINIC_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch transactions with 30-minute cache
 */
export function useTransactions(
  clinicId: string,
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  return useQuery({
    queryKey: financialKeys.transactionsWithRange(clinicId, startDate, endDate),
    queryFn: () => fetchTransactions(clinicId, startDate, endDate),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.CLINIC_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch payroll with 30-minute cache
 */
export function usePayroll(clinicId: string, month: string, enabled = true) {
  return useQuery({
    queryKey: financialKeys.payroll(clinicId, month),
    queryFn: () => fetchPayroll(clinicId, month),
    enabled: enabled && !!clinicId && !!month,
    staleTime: STALE_TIMES.HR_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Prefetch financial dashboard for faster navigation
 */
export function usePrefetchFinancialDashboard() {
  const queryClient = useQueryClient()
  
  return (clinicId: string, period: string) => {
    queryClient.prefetchQuery({
      queryKey: financialKeys.dashboard(clinicId, period),
      queryFn: () => fetchDashboard(clinicId, period),
      staleTime: STALE_TIMES.CLINIC_DATA,
    })
  }
}
